import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Moon, Clock, AlertTriangle, Activity } from 'lucide-react';

interface NightActivity {
  hour: string;
  movement: number;
  duration: number;
  location: string;
  type: 'BATHROOM' | 'WANDERING' | 'RESTLESS' | 'OTHER';
}

interface ActivitySummary {
  totalEvents: number;
  bathroomVisits: number;
  wanderingEvents: number;
  restlessEvents: number;
  averageDuration: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
}

const NightTimeActivity: React.FC = () => {
  // Mock data - in real app this would come from floor sensors
  const hourlyData = [
    { time: '10 PM', activity: 2, bathroom: 1, wandering: 0, restless: 1 },
    { time: '11 PM', activity: 1, bathroom: 0, wandering: 0, restless: 1 },
    { time: '12 AM', activity: 0, bathroom: 0, wandering: 0, restless: 0 },
    { time: '1 AM', activity: 3, bathroom: 1, wandering: 1, restless: 1 },
    { time: '2 AM', activity: 1, bathroom: 0, wandering: 0, restless: 1 },
    { time: '3 AM', activity: 2, bathroom: 1, wandering: 0, restless: 1 },
    { time: '4 AM', activity: 1, bathroom: 0, wandering: 0, restless: 1 },
    { time: '5 AM', activity: 2, bathroom: 1, wandering: 0, restless: 1 },
    { time: '6 AM', activity: 3, bathroom: 1, wandering: 0, restless: 2 }
  ];

  const recentActivities: NightActivity[] = [
    { hour: '1:15 AM', movement: 3, duration: 8, location: 'Bathroom', type: 'BATHROOM' },
    { hour: '3:20 AM', movement: 2, duration: 5, location: 'Hallway', type: 'WANDERING' },
    { hour: '4:45 AM', movement: 1, duration: 3, location: 'Bedroom', type: 'RESTLESS' }
  ];

  const activitySummary: ActivitySummary = {
    totalEvents: 15,
    bathroomVisits: 5,
    wanderingEvents: 2,
    restlessEvents: 8,
    averageDuration: 5.5,
    riskLevel: 'MEDIUM'
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'HIGH':
        return 'text-red-500';
      case 'MEDIUM':
        return 'text-yellow-500';
      case 'LOW':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Nighttime Activity Monitor</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Moon className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Total Events</h3>
          </div>
          <p className="text-3xl font-bold">{activitySummary.totalEvents}</p>
          <p className="text-sm text-gray-500">Last night</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Clock className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Bathroom Visits</h3>
          </div>
          <p className="text-3xl font-bold">{activitySummary.bathroomVisits}</p>
          <p className="text-sm text-gray-500">Average duration: {activitySummary.averageDuration}min</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Activity className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Wandering Events</h3>
          </div>
          <p className="text-3xl font-bold">{activitySummary.wanderingEvents}</p>
          <p className="text-sm text-gray-500">Detected last night</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <AlertTriangle className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Risk Level</h3>
          </div>
          <p className={`text-3xl font-bold ${getRiskColor(activitySummary.riskLevel)}`}>
            {activitySummary.riskLevel}
          </p>
          <p className="text-sm text-gray-500">Based on activity patterns</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Hourly Activity Pattern</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="bathroom" name="Bathroom Visits" fill="#8b5cf6" />
              <Bar dataKey="wandering" name="Wandering" fill="#f59e0b" />
              <Bar dataKey="restless" name="Restless Events" fill="#ef4444" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Activity Timeline</h3>
          <div className="space-y-4">
            {recentActivities.map((activity, index) => (
              <div key={index} className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  {activity.type === 'BATHROOM' && <Clock className="text-purple-500" />}
                  {activity.type === 'WANDERING' && <Activity className="text-yellow-500" />}
                  {activity.type === 'RESTLESS' && <AlertTriangle className="text-red-500" />}
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">{activity.hour}</span>
                    <span className="text-sm text-gray-500">({activity.duration} minutes)</span>
                  </div>
                  <p className="text-gray-600">{activity.type.toLowerCase()} activity detected in {activity.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Activity Insights</h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Clock className="text-purple-500" />
            </div>
            <div>
              <h4 className="font-medium">Bathroom Visit Pattern</h4>
              <p className="text-gray-600">Most bathroom visits occur between 1 AM and 3 AM. Consider adjusting evening fluid intake.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Activity className="text-yellow-500" />
            </div>
            <div>
              <h4 className="font-medium">Wandering Behavior</h4>
              <p className="text-gray-600">Two instances of wandering detected. Recommend reviewing evening routine and bedroom environment.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="text-red-500" />
            </div>
            <div>
              <h4 className="font-medium">Sleep Quality Impact</h4>
              <p className="text-gray-600">Frequent nighttime activity may be affecting sleep quality. Consider sleep hygiene assessment.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NightTimeActivity; 